import { ArrowForward } from '@mui/icons-material'
import { Button, Card } from '@mui/material'
import Box from '@mui/material/Box'
import Step from '@mui/material/Step'
import StepContent from '@mui/material/StepContent'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import { usePlanningStore } from '@planning/AppProvider'
import { InitializationWrapper } from '@planning/components/InitializationWrapper'
import { ICrane, VesselTallyViewStore } from '@planning/rt-stores/tallyman/VesselTallyViewStore'
import { IVesselVisitItem } from '@planning/rt-stores/vesselVisit/VesselVisitItem'
import { useNavigationStore } from '@planning/stores'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { slugify } from '@tom-ui/utils'
import { reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'
import VesselIcon from './VesselIcon'
import { VesselVisitLabel } from './atoms/VesselVisitLabel'

export type VesselVisitOperation =
  | 'Discharge'
  | 'Load'
  | 'HatchCoverMove'
  | 'Stoppage'
  | 'ShowVisitDetails'
interface IStepItemProps<T> {
  label: React.ReactNode
  showIcon?: boolean
  data: T
  testId?: string
  onSelect?: (item: T) => void
}

const StepItem = <_, T>(props: IStepItemProps<T>) => {
  const theme = useMinimalsTheme()

  return (
    props.label && (
      <Card
        data-cy={props.testId}
        sx={{
          p: '1rem',
          m: '.5rem 0',
          borderRadius: theme.customRadius.medium,
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
        onClick={() => props.onSelect?.(props.data)}
      >
        {(typeof props.label === 'string' && (
          <Typography fontWeight='bold'>{props.label}</Typography>
        )) ||
          props.label}
        {props.showIcon && <VesselIcon />}
      </Card>
    )
  )
}

const SelectedItem: FC<{ title: string; subtitle: React.ReactNode; handleBack: () => void }> = ({
  title,
  subtitle,
  handleBack,
}) => {
  const theme = useMinimalsTheme()

  return (
    <Card
      sx={{
        p: '1rem',
        m: '-2rem .5rem .5rem 2rem',
        borderRadius: theme.customRadius.medium,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'space-between',
      }}
      onClick={() => {
        handleBack()
      }}
    >
      <Box>
        <Typography fontSize={'.8rem'}>{title}</Typography>
        {(typeof subtitle === 'string' && <Typography fontWeight='bold'>{subtitle}</Typography>) ||
          subtitle}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography>Edit</Typography>
      </Box>
    </Card>
  )
}

interface IProps {
  store: VesselTallyViewStore
  onSelectOperation: (op: VesselVisitOperation) => void
  onVesselSelection: (id: number) => void
  onCraneSelection: (id: number) => void
}

export const VesselSelectionStepper: FC<IProps> = observer(
  ({ store, onSelectOperation, onVesselSelection, onCraneSelection }) => {
    const { appViewStore, vesselVisitItemStore } = usePlanningStore()
    const { t } = useTranslate()
    const theme = useMinimalsTheme()
    const navigationStore = useNavigationStore()

    const [activeStep, setActiveStep] = useState(0)

    const { totalDischargeConfirmed, totalLoadConfirmed, totalDischargeOrders, totalLoadOrders } =
      store

    useEffect(() => {
      if (store.selectedCrane) setActiveStep(2)
      else if (store.selectedVisitId) setActiveStep(1)
      else setActiveStep(0)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.selectedVisitId, store.selectedCrane])

    const handleNext = () => setActiveStep(prevActiveStep => prevActiveStep + 1)
    const handleBack = () => setActiveStep(prevActiveStep => prevActiveStep - 1)

    const handleSelectVesselVisit = (item: IVesselVisitItem) => {
      store.selectVesselVisit(item.id)
      onVesselSelection(item.id)
      handleNext()
    }

    const handleSelectCrane = (item: ICrane) => {
      store.selectCrane(item)

      onCraneSelection(item.id)

      handleNext()
    }

    reaction(
      () => {
        return {
          selectedVisit: store.selectedVisit,
          isInOperation: store.isSelectedVisitInOperation,
        }
      },
      reactData => {
        if (
          reactData.selectedVisit &&
          !reactData.isInOperation &&
          navigationStore.currentStep === 1
        ) {
          store.selectCrane()
          store.selectVesselVisit()
          appViewStore.setShowAlert('warning', t('operationStopped', 'Operation stopped.'))
        }
      },
    )

    return (
      <InitializationWrapper isInitializing={!vesselVisitItemStore.hasBeenInitialized}>
        <Box sx={{ width: '100%' }}>
          <Stepper activeStep={activeStep} orientation='vertical' connector={null}>
            <Step>
              <StepLabel>{activeStep !== 1 ? t('Vessel selection') : ''} </StepLabel>

              <StepContent>
                {store.vesselVisits
                  .filter(
                    item =>
                      item.discharge.orderCounts.container.confirmed <
                        item.discharge.orderCounts.container.total ||
                      item.load.orderCounts.container.confirmed <
                        item.load.orderCounts.container.total,
                  )
                  .map((item, index) => {
                    return (
                      <StepItem
                        testId={`vessel-visit-item-${slugify(item.vessels[0]?.data.name ?? 'unknown')}`}
                        key={index}
                        label={<VesselVisitLabel item={item} />}
                        data={item}
                        onSelect={handleSelectVesselVisit}
                        showIcon
                      />
                    )
                  })}
              </StepContent>

              {activeStep >= 1 && store.selectedVisit ? (
                <SelectedItem
                  title={t('vesselSelection', 'Vessel selection')}
                  subtitle={<VesselVisitLabel item={store.selectedVisit} />}
                  handleBack={() => setActiveStep(0)}
                />
              ) : (
                ''
              )}
            </Step>

            {/* Crane selection */}
            <Step>
              <StepLabel>{t('craneSelection', 'Crane selection')}</StepLabel>
              <StepContent>
                {store.cranes.map((item, index) => {
                  return (
                    <StepItem
                      testId='crane-visit-item'
                      key={index}
                      label={item.name}
                      data={item}
                      onSelect={handleSelectCrane}
                    />
                  )
                })}
              </StepContent>

              {activeStep === 2 && store.selectedCrane && (
                <SelectedItem
                  title={t('selectedCrane', 'Selected crane')}
                  subtitle={store.selectedCrane.name}
                  handleBack={handleBack}
                />
              )}
            </Step>

            {/* Operation selection */}
            <Step>
              <StepLabel>{t('operationSelection', 'Operation selection')}</StepLabel>
              <StepContent>
                <Button
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                    color: theme.palette.common.black,
                  }}
                  color='secondary'
                  onClick={() => onSelectOperation('ShowVisitDetails')}
                  data-cy='show-visit-details-btn'
                >
                  {t('showVisitDetails', 'Show visit details')}
                  <ArrowForward />
                </Button>
                {store.selectedVisit && totalDischargeConfirmed < totalDischargeOrders ? (
                  <StepItem
                    testId='discharge-operation-item'
                    label={'Discharge'}
                    data={'Discharge'}
                    onSelect={onSelectOperation}
                  />
                ) : (
                  ''
                )}

                {store.selectedVisit && totalLoadConfirmed < totalLoadOrders ? (
                  <StepItem
                    testId='load-operation-item'
                    label={'Load'}
                    data={'Load'}
                    onSelect={onSelectOperation}
                  />
                ) : (
                  ''
                )}
                {store.selectedVisit && (
                  <StepItem
                    testId='hatch-cover-operation-item'
                    label={t('hatchCoverMoves', 'Hatch cover moves')}
                    data={'HatchCoverMove'}
                    onSelect={onSelectOperation}
                  />
                )}
                {store.selectedVisit && (
                  <StepItem
                    testId='stoppage-recording-item'
                    label={t('stoppage', 'Stoppage')}
                    data={'Stoppage'}
                    onSelect={onSelectOperation}
                  />
                )}
              </StepContent>
            </Step>
          </Stepper>
        </Box>
      </InitializationWrapper>
    )
  },
)
